import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Typography, Button, Grid2 } from "@mui/material";
import { fetchChannelData, fetchSchema } from "../../utilities/apiRequests";
import HeaderBar from "../Shared/HeaderBar/HeaderBar";
import NavigationBar from "../Shared/NavigationBar/NavigationBar.js"
import EditRow from './EditRow';
import ConfirmationScreen from "./ConfirmationScreen";
import { getPlatform, useScreenOrientation } from "../../utilities/orientationAndPlatform.js";


export default function EditPage(){

    let [newValues, setNewValues] = useState([])
    let [rowErrorStates, setRowErrorStates] = useState([])

    let [pageErrorState, setPageErrorState] = useState(false)
    let [noChangesStagedState, setNoChangesStagedState] = useState(false)

    let [confirmationScreenState, setConfirmationScreenState] = useState(false)
    const closeConfirmationScreen = () => setConfirmationScreenState(false)

    //orientation hook
    const orientation = useScreenOrientation()
    
    //authenticate
    const { getAccessTokenSilently } = useAuth0();
    const { channelID } = useParams();
    const navigate = useNavigate();

    // fetch schema and channelData from api
    let [apiResponses, setApiResponses] = useState({schema:null, channelData:null})
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let fetchedChannelData = ""
        let fetchedSchema = ""

        async function fetchApiResponses()  {
            await getAccessTokenSilently({ authorizationParams: { scope: "write:channels" } })
                .then(async (accessToken) => fetchSchema(accessToken))
                .then((schemaResponse) => (fetchedSchema=schemaResponse))
                .catch((error) => {
                    console.error("Error fetching schema:", error);
                    navigate("/stationspage");
                    setError(true);
                })

            await getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
                .then(async (accessToken) => fetchChannelData(channelID, accessToken))
                .then((channelDataResponse) => (fetchedChannelData=channelDataResponse))
                .catch((error) => {
                    console.error("Error fetching channel data:", error);
                    navigate("/stationspage");
                    setError(true);
                })
            
            setApiResponses({schema:fetchedSchema, channelData:fetchedChannelData})
            setLoading(false)
        };

        fetchApiResponses()
    }, [getAccessTokenSilently, navigate, channelID]);


    let schema = apiResponses.schema
    let channelData = apiResponses.channelData

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }
    if (error) return;

    //set styling according to platform and screen orientation
    let platform = getPlatform()
    let titleFontSize = ''
    if (platform === 'mobile' && orientation === 0) titleFontSize = '3vh'
    else if (platform === 'mobile' && orientation === 90) titleFontSize = '3vw'
    else if (platform === 'desktop') titleFontSize = 25

    
    //different border styles for different page states 
    let pageBorderStyle = ''
    if (pageErrorState === true) pageBorderStyle = 'solid red'
    else if (noChangesStagedState === true) pageBorderStyle = '1.5px dashed gray'


    let channelAttributeNames = Object.keys(schema.properties)

    let contactsIndex = channelAttributeNames.indexOf("station_contacts")    //puts 'station_contact' field as last EditRow
    if (contactsIndex !== -1) channelAttributeNames.splice(contactsIndex, 1)
    channelAttributeNames.push('station_contacts')

    let oldValues = []
    let newValuesTemp = []
    let rowErrorStatesTemp = []
    let nameToIndex = {}
    for(let i = 0; i < channelAttributeNames.length; i++){
        let channelAttributeName = channelAttributeNames[i]
        let channelAttributeValue = channelData[channelAttributeName]

        if (channelAttributeValue === undefined) channelAttributeValue = ""

        if (channelAttributeValue === true) channelAttributeValue = 'True'
        else if (channelAttributeValue === false) channelAttributeValue = 'False'

        oldValues.push(channelAttributeValue)
        newValuesTemp.push(channelAttributeValue)
        rowErrorStatesTemp.push(true)
        nameToIndex[channelAttributeName] = i
    }

    if (newValues.length === 0) setNewValues([...newValuesTemp])
    if (rowErrorStates.length === 0) setRowErrorStates([...rowErrorStatesTemp])    

    function updateNewValues(newValueIndex, newValue) {
        newValues[newValueIndex] = newValue
        setNewValues([...newValues])
    }

    function updateRowErrorStates(rowErrorIndex, errorValue) {
        rowErrorStates[rowErrorIndex] = errorValue
        setRowErrorStates([...rowErrorStates])
    }

    function submitButtonOnClick(event){
        event.preventDefault()
        if (rowErrorStates.includes(false)) setPageErrorState(true)
        else if (JSON.stringify(oldValues) === JSON.stringify(newValues)) setNoChangesStagedState(true);
        else if (JSON.stringify(oldValues) !== JSON.stringify(newValues)) setConfirmationScreenState(true)
    }   

    function resetPageState(event){
        event.preventDefault()
        if (event.target.id !== "SubmitButton") { 
            setPageErrorState(false)
            setNoChangesStagedState(false)
        }
    }


    let market = (channelData.market) ? channelData.market : ""
    let call_sign = (channelData.call_sign) ? channelData.call_sign : ""
    let affiliation = (channelData.affiliation) ? " (" + channelData.affiliation  + ")" : ""

    let pageTitle = market + " " + call_sign + affiliation

    return (
        <>
            <HeaderBar page={'EditPage'} channelID={channelID}></HeaderBar>
            <Grid2 container={true} justifyContent={'center'} alignItems={'center'} direction={'column'} onClick={resetPageState} id={"EditPageGrid"}>
                <Typography fontWeight={'bold'} textAlign={'center'} sx={{fontSize: titleFontSize}} id={"title"}>{pageTitle}</Typography>

                <Box paddingLeft={'1em'} paddingRight={'1em'} sx={{border: pageBorderStyle, '& > :last-child':{marginBottom: '10em'}}} id={"InputsBox"}>
                    {channelAttributeNames.map((channelAttributeName) => (
                        <EditRow 
                            channelAttributeIndex={nameToIndex[channelAttributeName]}
                            channelAttributeName={channelAttributeName}
                            oldValue={channelData[channelAttributeName]}
                            updateNewValues={updateNewValues}
                            updateRowErrorStates={updateRowErrorStates}
                        />
                    ))}

                    {(pageErrorState === true) && (
                        <Typography textAlign={'center'} color="error">Must Lock All Boxes</Typography>
                    )}

                    {(pageErrorState === false && noChangesStagedState === true) && (
                        <Typography textAlign={'center'} sx={{color:"gray"}}>No Changes Staged</Typography>
                    )}

                    {(confirmationScreenState === true) && (
                        <ConfirmationScreen
                            confirmationScreenState={confirmationScreenState}
                            closeConfirmationScreen={closeConfirmationScreen}
                            oldValues={oldValues}
                            newValues={newValues}
                            channelAttributeNames={channelAttributeNames}
                            channelID={channelID}
                            nameToIndex={nameToIndex}
                        />
                    )}

                </Box>
            </Grid2>

            <Box position={"fixed"} bottom={"0"} width={"100%"} zIndex={100} textAlign={'center'} backgroundColor={"var(--mui-palette-background-default)"} id={"SubmitButtonBox"}>
                <Button variant={"contained"} zIndex={101} id={"SubmitButton"} sx={{backgroundColor: "primary.main"}} onClick={submitButtonOnClick}>Confirm</Button>
                <NavigationBar page={"EditPage"}></NavigationBar>
            </Box>
        </>
    )
}
